import cx from "classnames";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import TurndownService from "turndown";
import GroupUtil from "../../../../_utils/extensions/group-util";
import { SectionUtil } from "../../../../_utils/extensions/section.utils";
import I18 from "../../../atoms/i18";
import TextButton from "../../../atoms/text-button";
import {
  ConvertToSlugString,
  CreateSlugComponent,
} from "../../../create-slug-modal";
import { Checkbox } from "../../../form-controls/checkbox.component";
import IconButton2 from "../../../icon-button-2-component";
import { Modal } from "../../../modal.component";
import RightSideModal from "../../../right-side-modal.component";
import Label from "../../../typography/label.typography";
import UIcon from "../../../uicon-component";

const tds = new TurndownService();

/**
 * Post create component header
 * @param {boolean} isEditEnabled - boolean to determine if edit is enabled. If true, Edit post header is shown.
 * @param {boolean} setActive - function to set active of create post modal state.
 * @param {object} community - community object
 * @param {boolean} slugModalActive - boolean to determine slug modal active state
 * @param {Function} setSlugModalActive - function to set slug modal active state
 * @param {string} title - post title string
 * @param {string} description - post description string
 * @param {string} slug - post slug string
 * @param {Function} setSlug - function to set slug
 * @param {string} slugTitle - post slug title string
 * @param {Function} setSlugTitle - function to set slug title
 * @param {string} slugDescription - post slug description string
 * @param {Function} setSlugDescription - function to set slug description
 * @param {string} OGTitle - post OG title string
 * @param {Function} setOGTitle - function to set OG title
 * @param {string} OGDescription - post OG description string
 * @param {Function} setOGDescription - function to set OG description
 * @param {string} OGImage - post OG image string
 * @param {Function} setOGImage - function to set OG image
 * @param {Function} clearSlugMeta - function to clear slug meta
 * @param {Array<Object>} groups - List of groups of community
 * @param {object} selectedGroup - selected group from header dropdown | passed from Channel
 * @param {Function} setSelectedGroup - function to set selected group
 * @param {object} selectedTab - selected tab from header dropdown | passed from Channel
 * @param {Function} setSelectedTab - function to set selected tab
 * @param {boolean} disablePostInAnotherGroup - boolean to enable/disable group dropdown. Should be true if post is being edited or post id being created from Channel. Should be false if post is created from community home page. .
 */
export default function PostCreateHeader({
  isEditEnabled = false,
  setActive,
  community,
  slugModalActive,
  setSlugModalActive,
  slug,
  title,
  setSlug,
  slugTitle,
  setSlugTitle,
  slugDescription,
  description,
  setSlugDescription,
  OGTitle,
  setOGTitle,
  OGDescription,
  setOGDescription,
  OGImage,
  setOGImage,
  clearSlugMeta,
  selectedGroup,
  setSelectedGroup,
  selectedTab,
  setSelectedTab,
  groups,
  disablePostInAnotherGroup = false,
  // Post Customization
  sendEmailNotification,
  setSendEmailNotification,
  sendPushNotification,
  setSendPushNotification,
  hideLike,
  setHideLike,
  hideComment,
  setHideComment,
  hideCreator,
  activeModal,
  setActiveModal,
  setHideCreator,
  customDate,
  setCustomDate,
  hideDate,
  setHideDate,
}) {
  return (
    <div className="PostCreateHeader flex justify-between align-items-center px-4 py-3 sticky top-0 theme-bg-surface z-10">
      <div className="flex space-x-2 items-center">
        <h5 className="font-semibold">
          {isEditEnabled ? (
            <I18>Update Post</I18>
          ) : (
            <I18>Create new post in</I18>
          )}{" "}
        </h5>
        {!isEditEnabled && (
          <h3 className="flex items-center ">
            <TextButton
              label={selectedTab?.name ?? "Select Channel"}
              disabled={disablePostInAnotherGroup}
              onClick={() => {
                setActiveModal(true);
              }}
            />
            <SelectGroupTabToPost
              groups={groups}
              active={activeModal}
              setActive={setActiveModal}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              selectedGroup={selectedGroup}
              setSelectedGroup={setSelectedGroup}
            />
          </h3>
        )}
      </div>
      <div className="flex space-x-4 items-center">
        {/* Display settings icon only to Group admin and moderator */}
        {selectedGroup &&
          ["admin", "moderator"].includes(selectedGroup.myRole) && (
            <button
              onClick={() => {
                setSlugModalActive(!slugModalActive);
              }}>
              <UIcon icon="settings" className="cursor-pointer" />
            </button>
          )}

        <button
          onClick={(_e) => {
            setActive(false);
          }}>
          <UIcon icon="cross" className="cursor-pointer" w-12 />
        </button>
      </div>
      {slugModalActive && (
        <RightSideModal
          active={slugModalActive}
          setActive={setSlugModalActive}
          width={600}>
          <div className="flex flex-col max-h-screen overflow-auto theme-bg-surface pb-20">
            {/* HEADER */}
            <div className="sticky top-0 border-b theme-bg-surface flex items-center py-2  backdrop-blur-sm z-10">
              <IconButton2
                icon="angle-left"
                solid
                className="mt-0.5"
                size="xs"
                onClick={() => {
                  setSlugModalActive(false);
                }}
              />
              <h3 className="font-semibold">
                <I18>Settings</I18>
              </h3>
            </div>

            <div className="flex flex-col  px-5 py-5">
              <span className="flex items-center space-x-2 text-xs font-semibold">
                <I18>Post Customization</I18>
                <span>
                  <UIcon icon="caret-down" size="xs" className="ml-1" />
                </span>
              </span>
              <div className="flex flex-col space-y-3 mb-5 border theme-border-default px-4 py-3 my-2 rounded theme-bg-default">
                <div className="flex flex-col space-y-4">
                  <Checkbox
                    selected={hideLike}
                    onClick={() => {
                      setHideLike(!hideLike);
                    }}>
                    <span className="pt-3">
                      <I18>Disable Likes</I18>
                    </span>
                  </Checkbox>
                  <Checkbox
                    selected={hideComment}
                    onClick={() => {
                      setHideComment(!hideComment);
                    }}>
                    <I18>Hide Comment</I18>
                  </Checkbox>
                  <Checkbox
                    selected={hideCreator}
                    onClick={() => {
                      setHideCreator(!hideCreator);
                      if (!hideCreator) {
                        setHideDate(true);
                      }
                    }}>
                    <I18>Hide profile from post</I18>
                  </Checkbox>
                  <Checkbox
                    selected={hideDate}
                    disabled={hideCreator}
                    onClick={() => {
                      if (!hideCreator) {
                        setHideDate(!hideDate);
                      }
                    }}>
                    <span
                      className={cx("", {
                        "theme-text-subtitle-2": hideCreator,
                      })}>
                      <I18>Hide post time</I18>
                    </span>
                  </Checkbox>
                  <div className="flex flex-col space-y-2 mt-2">
                    <h4 className="font-semibold">
                      <I18>Custom date for post</I18>
                    </h4>
                    <div className="flex items-center theme-bg-default border theme-border-default w-52 py-2 px-2 outline-none rounded cursor-pointer theme-bg-surface">
                      <DatePicker
                        className=" w-24 outline-none"
                        selected={customDate}
                        maxDate={new Date()}
                        onChange={(date) => {
                          setCustomDate(new Date(date));
                        }}
                        shouldCloseOnSelect={true}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        timeCaption="time"
                        placeholderText="Select date"
                        dateFormat="dd - MM - yyyy"
                      />
                      <div className="w-1 h-4 border-r mr-1 theme-border-default" />
                      <div className=" rounded select-none">
                        <div className="text-center theme-text-heading-2">
                          {customDate && format(customDate, " h:mm a")}
                          {!customDate && (
                            <h4 className="theme-text-subtitle-2 p-0">
                              <I18>time</I18>
                            </h4>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notify users */}
              {setSendEmailNotification ? (
                <div className="pb-2">
                  <Checkbox
                    selected={sendEmailNotification}
                    onClick={() => {
                      setSendEmailNotification(!sendEmailNotification);
                    }}>
                    <span className="pt-3">
                      <I18>Send Email Notification</I18>
                    </span>
                  </Checkbox>
                </div>
              ) : (
                <></>
              )}
              {setSendPushNotification ? (
                <div className="pb-2">
                  <Checkbox
                    selected={sendPushNotification}
                    onClick={() => {
                      setSendPushNotification(!sendPushNotification);
                    }}>
                    <span className="pt-3">
                      <I18>Send Push Notification</I18>
                    </span>
                  </Checkbox>
                </div>
              ) : (
                <></>
              )}

              <CreateSlugComponent
                community={community}
                active={slugModalActive}
                setActive={setSlugModalActive}
                slug={slug === null ? ConvertToSlugString(title) : slug}
                slugPrefix="p"
                setSlug={setSlug}
                title={slugTitle === null ? title : slugTitle}
                setTitle={setSlugTitle}
                description={
                  slugDescription === null
                    ? tds.turndown(description)
                    : slugDescription
                }
                setDescription={setSlugDescription}
                OGTitle={OGTitle === null ? title : OGTitle}
                setOGTitle={setOGTitle}
                OGDescription={
                  OGDescription === null
                    ? tds.turndown(description)
                    : OGDescription
                }
                setOGDescription={setOGDescription}
                OGImage={OGImage}
                setOGImage={setOGImage}
                OgImageSubtitle={
                  "By default it will take up the image uploaded with the post"
                }
                clearSlugMeta={clearSlugMeta}
              />
            </div>
          </div>
        </RightSideModal>
      )}
    </div>
  );
}

/**
 * Component to select group | Channel to post in
 * @param {Array<Object>} groups - List of groups of community.
 * @param {boolean} active - boolean to determine modal active state.
 * @param {Function} setActive - function to set active state of SelectGroupTabToPostModal.
 * @param {object} selectedTab - Previously selected tab.
 * @param {Function} setSelectedTab - function to set selected tab.
 * @param {Function} setSelectedGroup - function to set selected group.
 * @param {object} selectedGroup - Previously selected group.
 */
function SelectGroupTabToPost({
  groups,
  active,
  setActive,
  selectedTab,
  setSelectedTab,
  setSelectedGroup,
  selectedGroup,
}) {
  if (!active) {
    return <></>;
  }
  const tab = selectedTab;

  const isGroupAvailable = groups && groups.length > 0;

  const isPartOfAnyGroup = groups?.some(
    (group) =>
      new GroupUtil(group).hasReadAccess() || group.groupType === "open"
  );

  return (
    <Modal active={active} setActive={setActive} overflow="" padding={false}>
      <div className="flex flex-col justify-end py-1">
        {/* HEADER */}
        <div className="flex px-4 justify-between theme-bg-surface border-b theme-border-default">
          <p className="font-bold text-md">
            <I18>Select channel</I18>{" "}
          </p>
          <IconButton2
            icon="cross"
            hoverable
            size="md"
            onClick={() => {
              setActive(false);
            }}
          />
        </div>
        <div>
          {/* BODY */}
          <div className="flex flex-col justify-end ">
            <div
              className="overflow-y-auto max-h-min theme-bg-surface rounded m-5 "
              style={{ maxHeight: "60vh" }}>
              {isGroupAvailable ? (
                isPartOfAnyGroup ? (
                  groups
                    .filter(
                      (group) =>
                        group.joinStatus === "joined" ||
                        group.groupType === "open"
                    )
                    .map((group, index) => {
                      // Filter out private tabs if user is not admin or moderator
                      const tabs = group?.tabs?.filter(
                        (tab) =>
                          !(
                            !tab?.isJoined &&
                            tab?.isPrivate &&
                            !["admin", "moderator"].includes(group.myRole)
                          )
                      );
                      return (
                        <div
                          key={index}
                          className="py-1 px-4 theme-bg-surface rounded">
                          <div className="theme-text-heading-1 text-md font-semibold py-2 ">
                            {group.name}
                          </div>
                          {tabs.map((tempTab, index) => {
                            // Check if user can create post in this tab or not
                            const disable = !new SectionUtil(
                              tempTab
                            ).canCreatePost(group);

                            return (
                              <div key={index} className=" mt-1">
                                <div
                                  className={cx("p-2 rounded", {
                                    "disabled cursor-not-allowed bg-opacity-10":
                                      disable,
                                    "hover:theme-bg-default cursor-pointer font-semibold":
                                      !disable,
                                    "theme-bg-primary-light":
                                      tab?.id === tempTab.id,
                                  })}
                                  selected={tab?.id === tempTab.id}
                                  disabled={disable}
                                  onClick={() => {
                                    if (disable) {
                                      return;
                                    }
                                    // if (tab?.id == tempTab.id) {
                                    //   setTab(undefined);
                                    //   setGroup(undefined);
                                    // } else {
                                    //   setTab(tempTab);
                                    //   setGroup(group);
                                    // }
                                    setSelectedGroup(group);
                                    setSelectedTab(tempTab);
                                    setActive(false);
                                  }}>
                                  <div
                                    className={cx("flex items-center text-sm", {
                                      "theme-text-subtitle-2": disable,
                                      "theme-text-heading-2": !disable,
                                    })}>
                                    <div
                                      className={cx("emoji", {
                                        hash:
                                          !tempTab.emoji ||
                                          tempTab.emoji === "#",
                                      })}>
                                      {tempTab.emoji ? tempTab.emoji : "#"}
                                    </div>
                                    <span className="pl-3">{tempTab.name}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                ) : (
                  <div className="p-12 rounded">
                    <div className="flex flex-col items-center place-content-center">
                      <Label translate variant="t1">
                        You are not part of any group or channel
                      </Label>
                      <Label translate variant="s1">
                        Join a group to create post
                      </Label>
                    </div>
                  </div>
                )
              ) : (
                <div className="p-12 flex items-center place-content-center rounded theme-text-subtitle-1">
                  No section available to create post
                </div>
              )}
            </div>

            {/* <div className="flex justify-end theme-bg-surface w-full p-2 border-t theme-border-default">
              <Button
                label="Select"
                disabled={!tab}
                onClick={() => {
                  setSelectedGroup(group);
                  setSelectedTab(tab);
                  setActive(false);
                }}
                large
              />
            </div> */}
          </div>
        </div>
      </div>
    </Modal>
  );
}
